import * as THREE from 'three';
import { Weapon } from './Weapon';
import { ProjectilePool } from './ProjectilePool';
import { LaserWeapon } from './LaserWeapon';
import { ChainGun } from './ChainGun';
import { Railgun, HitscanCallback } from './Railgun';
import { PlasmaCannon } from './PlasmaCannon';
import { HomingMissiles } from './HomingMissiles';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { EventBus } from '../core/EventBus';
import { WeaponUpgradeManager, WeaponUnlocks } from '../systems/WeaponUpgradeManager';

export class WeaponManager {
  private weapons: Weapon[] = [];
  private currentIndex: number = 0;
  private projectilePool: ProjectilePool;
  private targets: THREE.Object3D[] = [];
  private upgradeManager: WeaponUpgradeManager;
  private scene: THREE.Scene;
  private hitscanCallback: HitscanCallback | null = null;

  constructor(scene: THREE.Scene, physicsWorld: PhysicsWorld) {
    this.scene = scene;
    this.projectilePool = new ProjectilePool(scene, physicsWorld);
    this.upgradeManager = new WeaponUpgradeManager();

    // Initialize default weapons
    const laser = new LaserWeapon(this.projectilePool);
    const chaingun = new ChainGun(this.projectilePool);

    // Set upgrade manager on weapons
    laser.setUpgradeManager(this.upgradeManager);
    chaingun.setUpgradeManager(this.upgradeManager);

    this.weapons.push(laser);
    this.weapons.push(chaingun);

    // Listen for weapon unlock events
    EventBus.on('weapon:unlocked', (data) => {
      this.unlockWeapon(data.weapon as keyof WeaponUnlocks);
    });

    // Listen for ammo pickup events
    EventBus.on('player:ammo', (data) => {
      const weapon = this.getCurrentWeapon();
      if (weapon) {
        weapon.refillAmmo(data.amount);
        // Update HUD with new ammo count
        EventBus.emit('weapon:switched', {
          weaponType: weapon.getName(),
          ammo: weapon.getAmmo(),
          maxAmmo: weapon.getMaxAmmo(),
        });
      }
    });

    // Emit initial weapon state
    const currentWeapon = this.getCurrentWeapon();
    if (currentWeapon) {
      EventBus.emit('weapon:switched', {
        weaponType: currentWeapon.getName(),
        ammo: currentWeapon.getAmmo(),
        maxAmmo: currentWeapon.getMaxAmmo(),
      });
    }
  }

  public setHitscanCallback(callback: HitscanCallback): void {
    this.hitscanCallback = callback;
    // Update any existing railgun
    for (const weapon of this.weapons) {
      if (weapon instanceof Railgun) {
        weapon.setHitscanCallback(callback);
      }
    }
  }

  private unlockWeapon(weaponType: keyof WeaponUnlocks): void {
    let weapon: Weapon | null = null;

    switch (weaponType) {
      case 'railgun':
        if (!this.hasWeapon('Railgun')) {
          const railgun = new Railgun(this.scene);
          if (this.hitscanCallback) {
            railgun.setHitscanCallback(this.hitscanCallback);
          }
          weapon = railgun;
        }
        break;

      case 'plasmaCannon':
        if (!this.hasWeapon('Plasma Cannon')) {
          const plasma = new PlasmaCannon(this.scene);
          plasma.setTargets(this.targets);
          weapon = plasma;
        }
        break;

      case 'homingMissiles':
        if (!this.hasWeapon('Homing Missiles')) {
          const missiles = new HomingMissiles(this.scene);
          missiles.setTargets(this.targets);
          weapon = missiles;
        }
        break;
    }

    if (weapon) {
      this.addWeapon(weapon);
      // Switch to the new weapon
      this.currentIndex = this.weapons.length - 1;
      this.emitWeaponSwitch();
    }
  }

  public getCurrentWeapon(): Weapon | null {
    return this.weapons[this.currentIndex] ?? null;
  }

  public nextWeapon(): void {
    this.currentIndex = (this.currentIndex + 1) % this.weapons.length;
    this.emitWeaponSwitch();
  }

  public previousWeapon(): void {
    this.currentIndex =
      (this.currentIndex - 1 + this.weapons.length) % this.weapons.length;
    this.emitWeaponSwitch();
  }

  public selectWeapon(index: number): void {
    if (index >= 0 && index < this.weapons.length && index !== this.currentIndex) {
      this.currentIndex = index;
      this.emitWeaponSwitch();
    }
  }

  private emitWeaponSwitch(): void {
    const weapon = this.getCurrentWeapon();
    if (weapon) {
      EventBus.emit('weapon:switched', {
        weaponType: weapon.getName(),
        ammo: weapon.getAmmo(),
        maxAmmo: weapon.getMaxAmmo(),
      });
    }
  }

  public fire(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    const weapon = this.getCurrentWeapon();
    if (!weapon) return false;
    return weapon.fire(origin, direction);
  }

  public reload(): void {
    const weapon = this.getCurrentWeapon();
    if (weapon) {
      weapon.reload();
    }
  }

  public registerTarget(target: THREE.Object3D): void {
    this.targets.push(target);
    // Update weapons that track targets
    this.updateWeaponTargets();
  }

  public unregisterTarget(target: THREE.Object3D): void {
    const index = this.targets.indexOf(target);
    if (index !== -1) {
      this.targets.splice(index, 1);
      this.updateWeaponTargets();
    }
  }

  private updateWeaponTargets(): void {
    for (const weapon of this.weapons) {
      if (weapon instanceof PlasmaCannon) {
        weapon.setTargets(this.targets);
      } else if (weapon instanceof HomingMissiles) {
        weapon.setTargets(this.targets);
      }
    }
  }

  public update(dt: number): void {
    // Update upgrade manager (for buff timers)
    this.upgradeManager.update(dt);

    // Update all weapons (for reload timers)
    for (const weapon of this.weapons) {
      weapon.update();
    }

    // Update projectiles
    this.projectilePool.update(dt);

    // Check projectile collisions with targets
    this.projectilePool.checkCollisions(this.targets);
  }

  public getProjectilePool(): ProjectilePool {
    return this.projectilePool;
  }

  public getUpgradeManager(): WeaponUpgradeManager {
    return this.upgradeManager;
  }

  // Add a new weapon (used when unlocking new weapons)
  public addWeapon(weapon: Weapon): void {
    weapon.setUpgradeManager(this.upgradeManager);
    this.weapons.push(weapon);

    EventBus.emit('weapon:added', {
      weaponType: weapon.getName(),
      totalWeapons: this.weapons.length,
    });
  }

  // Check if a weapon type is already in the arsenal
  public hasWeapon(name: string): boolean {
    return this.weapons.some(w => w.getName() === name);
  }

  public destroy(): void {
    this.projectilePool.destroy();
  }
}
